import { createClient } from "@lib/supabase/client";

export type PushPlatform = "web" | "ios" | "android";

/**
 * Last device we ensured is registered, keyed by `${userId}\u0000${deviceId}\u0000${endpoint}`.
 * When the key matches we skip the RPC entirely: the server row is already exact,
 * so a call would only cost a round trip. Reset on sign-out so the next login
 * re-registers a device whose row was unbound.
 */
let lastEnsuredKey: string | null = null;
let inFlight: Promise<boolean> | null = null;
let syncGeneration = 0;

const pushTokenFetchInvalidators = new Set<() => void>();

function ensureKey(
  userId: string,
  deviceId: string,
  endpoint: string,
): string {
  return `${userId}\u0000${deviceId}\u0000${endpoint}`;
}

/**
 * Forgets the last-ensured marker, forcing a fresh RPC on the next sync.
 * Called on sign-out: unbind_push_subscription nulls the row's user_id, so the
 * next login must re-register even though the device and endpoint are unchanged.
 * Also clears any platform-level token-fetch throttle, since a fresh login must
 * re-read a current FCM token.
 */
export function resetPushSubscriptionSync(): void {
  syncGeneration += 1;
  lastEnsuredKey = null;
  inFlight = null;
  for (const invalidate of pushTokenFetchInvalidators) invalidate();
}

/**
 * Registers a callback that invalidates a platform token-fetch cache (e.g. the
 * web getToken throttle).
 */
export function registerPushTokenFetchInvalidator(
  invalidator: () => void,
): void {
  pushTokenFetchInvalidators.add(invalidator);
}

/**
 * Called when the push permission is observed as not granted, so the next grant
 * performs a fresh token fetch instead of reusing a throttled one.
 */
export function invalidatePushTokenFetch(): void {
  for (const invalidate of pushTokenFetchInvalidators) invalidate();
}

/**
 * Register (or refresh) this device's push subscription.
 *
 * Returns `true` when the server row already matched (no write) and `false`
 * when this call performed the insert/update. Either resolve means the device
 * is now registered; rejections are thrown as before.
 */
export async function upsertPushSubscription(params: {
  userId: string;
  deviceId: string;
  fcmToken: string;
  platform: PushPlatform;
  userAgent?: string | null;
}): Promise<boolean> {
  const key = ensureKey(params.userId, params.deviceId, params.fcmToken);

  if (lastEnsuredKey === key) return true;

  // Coalesce concurrent callers (banner + settings + bootstrap all fire at
  // mount) into a single RPC.
  if (inFlight) return inFlight;

  const generationAtStart = syncGeneration;
  const run: Promise<boolean> = (async () => {
    const supabase = createClient();
    const { data, error } = await supabase.rpc("upsert_push_subscription", {
      p_user_id: params.userId,
      p_device_id: params.deviceId,
      p_endpoint: params.fcmToken,
      p_user_agent: params.userAgent ?? null,
      p_platform: params.platform,
    } as never);

    if (error) {
      throw new Error(error.message || "Failed to store push subscription");
    }

    return data === true;
  })();

  inFlight = run;
  try {
    const alreadyExact = await run;
    // A successful call guarantees the row matches, so later syncs can skip.
    // Guard against a sign-out that raced this call.
    if (syncGeneration === generationAtStart) {
      lastEnsuredKey = key;
    }
    return alreadyExact;
  } finally {
    inFlight = null;
  }
}